/**
 * Firebase configuration + startup guard.
 *
 * Phase 01 verified empirically that `initializeApp({})` and
 * `getFirestore(app)` do NOT throw with an empty configuration: the
 * failure only surfaces at the first network call, which produces a
 * mysterious runtime error instead of an obvious misconfiguration.
 *
 * So we check the configuration ourselves, up front:
 *
 *   production:  missing configuration is fatal and renders an explicit
 *                 deployment error screen.
 *   development: missing configuration falls back to a local, in-browser
 *                 backend so the product can still be built and demoed,
 *                 with a persistent on-screen banner saying so.
 *
 * We never log configuration *values*, only which variable names are
 * missing. Firebase web config is not secret, but there is no reason to
 * print it either.
 */

const env = (key) => String(process.env[key] ?? "").trim();

/** Every variable Firebase needs before it can do anything useful. */
export const REQUIRED_ENV_VARS = Object.freeze([
  "REACT_APP_FB_API_KEY",
  "REACT_APP_FB_AUTH_DOMAIN",
  "REACT_APP_FB_PROJECT_ID",
  "REACT_APP_FB_APP_ID",
]);

/** Useful but not required to boot. */
export const OPTIONAL_ENV_VARS = Object.freeze([
  "REACT_APP_FB_STORAGE_BUCKET",
  "REACT_APP_FB_MESSAGING_SENDER_ID",
  "REACT_APP_FB_MEASUREMENT_ID",
]);

export const firebaseConfig = Object.freeze({
  apiKey: env("REACT_APP_FB_API_KEY"),
  authDomain: env("REACT_APP_FB_AUTH_DOMAIN"),
  projectId: env("REACT_APP_FB_PROJECT_ID"),
  storageBucket: env("REACT_APP_FB_STORAGE_BUCKET") || undefined,
  messagingSenderId: env("REACT_APP_FB_MESSAGING_SENDER_ID") || undefined,
  appId: env("REACT_APP_FB_APP_ID"),
  measurementId: env("REACT_APP_FB_MEASUREMENT_ID") || undefined,
});

/** Which required variables are absent. Never returns their values. */
export function getMissingConfigKeys() {
  return REQUIRED_ENV_VARS.filter((key) => !env(key));
}

export function isFirebaseConfigured() {
  return getMissingConfigKeys().length === 0;
}

export function isProductionBuild() {
  return process.env.NODE_ENV === "production";
}

/**
 * Startup decision for the whole application.
 *
 * @returns {{
 *   mode: "firebase" | "local" | "misconfigured",
 *   missing: string[],
 *   message: string
 * }}
 */
export function resolveBackendMode() {
  const missing = getMissingConfigKeys();

  if (missing.length === 0) {
    return { mode: "firebase", missing, message: "" };
  }

  if (isProductionBuild()) {
    return {
      mode: "misconfigured",
      missing,
      message:
        "Firebase configuration is missing from this build. " +
        "Set the required REACT_APP_FB_* variables and rebuild.",
    };
  }

  return {
    mode: "local",
    missing,
    message:
      "Firebase is not configured, so the app is running on a local " +
      "in-browser backend. Nothing is saved to the cloud and rooms cannot " +
      "be shared with other devices.",
  };
}

/** One loud, non-secret startup log so nothing fails silently. */
let hasWarned = false;
export function warnAboutConfigOnce() {
  if (hasWarned || process.env.NODE_ENV === "test") return;
  hasWarned = true;

  const { mode, missing, message } = resolveBackendMode();
  if (mode === "firebase") return;

  const detail = `Missing: ${missing.join(", ")}`;
  if (mode === "misconfigured") {
    // eslint-disable-next-line no-console
    console.error(`[I Don't Know Football] ${message} ${detail}`);
  } else {
    // eslint-disable-next-line no-console
    console.warn(`[I Don't Know Football] ${message} ${detail}`);
  }
}
